import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import Layouts from "../../layouts/layouts";
import DataTable_Component from "../../components/vehiclerevenue/Data Table/index";

export default function VehicleRevenue() {
  const title = "Vehicle Revenue Summary";

  const [apiData, setApiData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalVehicles: 0,
  });
  const [totalPayout, setTotalPayout] = useState(0);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [leasingPartnerOptions, setLeasingPartnerOptions] = useState([]);
  const [totalTrips, setTotalTrips] = useState(0);
  const [totalFixedTrips, setTotalFixedTrips] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectVal, setSelectval] = useState([]);
  const [totalPpTrips, setTotalPpTrips] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loadingExport, setLoadingExport] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    month: undefined,
    year: undefined,
    search: "",
    fromDate: undefined,
    toDate: undefined,
    companies: [],
    feetValues: [],
    statuses: [],
    leasingPartners: [],
  });
  const [averagePayout, setAveragePayout] = useState(0);
  const [dateTotals, setDateTotals] = useState([]);
  const [totalUnitsConsumed, setTotalUnitsConsumed] = useState(0);
  const [averageUnitsConsumed, setAverageUnitsConsumed] = useState(0);
  const [totalChargingSessions, setTotalChargingSessions] = useState(0);
  const [totalChargingAmount, setTotalChargingAmount] = useState(0);
  const [averageChargingAmount, setAverageChargingAmount] = useState(0);
  const [totalLeaseAmount, setTotalLeaseAmount] = useState(0);
  const [averageLeaseAmount, setAverageLeaseAmount] = useState(0);
  const [sendingMail, setSendingMail] = useState(false);
  const [showMailSuccess, setShowMailSuccess] = useState(false);
  const selectedMonth = moment().month() + 1;
  const selectedYear = moment().year();
  const handleShow = (row) => {
    setOpenModal(!openModal);
    setSelectval(row);
  };
  /* ✅ SINGLE SOURCE OF TRUTH */
  const fetchData = async (
    page = 1,
    month = selectedMonth,
    year = selectedYear,
    search = "",
    limit = pagination.limit,
    from = fromDate,
    to = toDate,
    companies = [],
    feetValues = [],
    statuses = [],
    leasingPartners = [],
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/orders/vehicle-payout/summary`,
        {
          params: {
            page,
            limit,
            search,

            ...(companies.length > 0 && {
              company: companies.join(","),
            }),

            ...(feetValues.length > 0 && {
              feet: feetValues.join(","),
            }),
            ...(statuses.length > 0 && {
              status: statuses.join(","),
            }),
            ...(leasingPartners.length > 0 && {
              leasingPartner: leasingPartners.join(","),
            }),

            ...(from && to
              ? {
                  fromDate: from,
                  toDate: to,
                }
              : {
                  month,
                  year,
                }),
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.data?.success) return;
      const payoutSummary = response.data.payoutSummary || {};

      const vehicleSummary = response.data.payoutSummary?.vehicleSummary || [];
      const apiDateTotals = response.data.payoutSummary?.dateTotals || [];

      setApiData(vehicleSummary);
      setPagination(response.data.pagination || {});
      setTotalPayout(response.data.payoutSummary?.totalPayout || 0);
      setTotalTrips(response.data.payoutSummary?.totalTrips || 0);
      setTotalFixedTrips(response.data.payoutSummary?.fixedTrips || 0);
      setTotalPpTrips(response.data.payoutSummary?.ppTrips || 0);
      setTotalFixedTrips(payoutSummary.fixedTrips || 0);
      setTotalPpTrips(payoutSummary.ppTrips || 0);
      setAveragePayout(response.data.payoutSummary?.averagePayout || 0);
      setTotalUnitsConsumed(
        response.data.payoutSummary?.totalUnitsConsumed || 0,
      );
      setAverageUnitsConsumed(
        response.data.payoutSummary?.averageUnitsConsumed || 0,
      );
      setTotalChargingSessions(
        response.data.payoutSummary?.totalChargingSessions || 0,
      );
      setTotalChargingAmount(
        response.data.payoutSummary?.totalChargingAmount || 0,
      );
      setAverageChargingAmount(
        response.data.payoutSummary?.averageChargingAmount || 0,
      );
      setTotalLeaseAmount(response.data.payoutSummary?.totalLeaseAmount || 0);
      setAverageLeaseAmount(
        response.data.payoutSummary?.averageLeaseAmount || 0,
      );
      setDateTotals(apiDateTotals);

      /* 🔹 COLLECT UNIQUE DATES */
      const dateSet = new Set();
      vehicleSummary.forEach((vehicle) => {
        Object.keys(vehicle.dateSummary || {}).forEach((date) =>
          dateSet.add(date),
        );
      });

      const sortedDates = Array.from(dateSet).sort(
        (a, b) => new Date(a) - new Date(b),
      );

      /* 🔹 STATIC COLUMNS */
      const staticColumns = [
        {
          name: <b>Vehicle Number</b>,
          selector: (row) => row.numberplate,
          sortable: true,
          minWidth: "150px",
        },
        {
          name: (
            <div
              style={{
                backgroundColor: "#0d6efd",
                color: "#fff",
                padding: "8px 12px",
                border: "1px solid #084298",
                borderRadius: "4px",
                width: "100%",
                minWidth: "140px",
                textAlign: "center",
                fontSize: "13px",
                fontWeight: "bold",
                lineHeight: "1.3",
              }}
            >
              Total Payout
              <br />₹
              {Number(payoutSummary.totalPayout || 0).toLocaleString("en-IN")}
            </div>
          ),

          selector: (row) => Number(row.payout || 0),

          cell: (row) => (
            <div
              style={{
                width: "100%",
                textAlign: "center",
                fontSize: "14px",
                fontWeight: "500",
                whiteSpace: "nowrap",
              }}
            >
              ₹
              {Number(row.payout || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          ),

          center: true,

          width: "160px",

          style: {
            minWidth: "160px",
          },
        },

        {
          name: (
            <div
              style={{
                backgroundColor: "#0d6efd",
                color: "#fff",
                padding: "6px 10px",
                border: "1px solid #084298",
                borderRadius: "4px",
                width: "100%",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                Average Payout
              </div>
            </div>
          ),
          selector: (row) =>
            Number(row.averagePayout || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          center: true,
          minWidth: "150px",
        },
        {
          name: <b>Status</b>,
          cell: (row) => {
            const status = row.status?.toLowerCase();

            return (
              <button
                onClick={() => handleShow(row)}
                className={`btn px-2 py-1 ${
                  status === "active"
                    ? "btn-success"
                    : status === "inactive"
                      ? "btn-danger"
                      : status === "unassigned"
                        ? "btn-danger"
                        : status === "service_center"
                          ? "btn-warning"
                          : "btn-secondary"
                }`}
                style={{
                  minWidth: "90px",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                {row.status || "Unknown"}
              </button>
            );
          },
          sortable: true,
          selector: (row) => row.status || "",
          width: "150px",
        },
        {
          name: <b>Company</b>,
          selector: (row) => row.company,
          sortable: true,
          minWidth: "150px",
        },
        {
          name: <b>Leasing Partner</b>,
          selector: (row) => row.leasingPartner,
          sortable: true,
          minWidth: "150px",
        },
        {
          name: <b>Vehicle Feet</b>,
          selector: (row) => row.feet,
          sortable: true,
          minWidth: "150px",
        },
        {
          name: (
            <div
              style={{
                backgroundColor: "#f8d7da",
                border: "2px solid #dc3545",
                borderRadius: "6px",
                padding: "4px 8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "#842029",
                }}
              >
                Lease / Avg
              </div>

              <b>
                ₹{Number(payoutSummary.totalLeaseAmount || 0).toLocaleString()}
                <br />
                <small>
                  Avg ₹
                  {Number(payoutSummary.averageLeaseAmount || 0).toFixed(2)}
                </small>
              </b>
            </div>
          ),

          cell: (row) => (
            <div style={{ textAlign: "center", lineHeight: "1.3" }}>
              <strong>₹{Number(row.leaseAmount || 0).toLocaleString()}</strong>

              <br />

              <small className="text-muted">
                Avg ₹{Number(row.averageLeaseAmount || 0).toFixed(2)}
              </small>
            </div>
          ),

          sortable: true,
          center: true,
          minWidth: "170px",
        },
        {
          name: (
            <div
              style={{
                backgroundColor: "#fff3cd",
                border: "2px solid #ffc107",
                borderRadius: "6px",
                padding: "4px 8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "#856404",
                }}
              >
                Units / Sessions
              </div>

              <b>
                {Number(payoutSummary.totalUnitsConsumed || 0).toFixed(2)} /{" "}
                {payoutSummary.totalChargingSessions || 0}
              </b>
            </div>
          ),
          cell: (row) => (
            <div style={{ textAlign: "center", lineHeight: "1.3" }}>
              <strong>{Number(row.unitsConsumed || 0).toFixed(2)}</strong>
              <br />
              <small className="text-muted">
                {row.chargingSessions || 0} Session
                {(row.chargingSessions || 0) !== 1 ? "s" : ""}
              </small>
            </div>
          ),
          sortable: true,
          center: true,
          minWidth: "160px",
        },
        {
          name: (
            <div
              style={{
                backgroundColor: "#fff3cd",
                border: "2px solid #ffc107",
                borderRadius: "6px",
                padding: "4px 8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "#856404",
                }}
              >
                Amount / Avg
              </div>

              <b>
                ₹
                {Number(
                  payoutSummary.totalChargingAmount || 0,
                ).toLocaleString()}
                <br />
                <small>
                  Avg ₹
                  {Number(payoutSummary.averageChargingAmount || 0).toFixed(2)}
                </small>
              </b>
            </div>
          ),

          cell: (row) => (
            <div style={{ textAlign: "center", lineHeight: "1.3" }}>
              <strong>
                ₹{Number(row.chargingAmount || 0).toLocaleString()}
              </strong>

              <br />

              <small className="text-muted">
                Avg ₹{Number(row.averageChargingAmount || 0).toFixed(2)}
              </small>
            </div>
          ),

          sortable: true,
          center: true,
          minWidth: "170px",
        },

        {
          name: (
            <div
              style={{
                backgroundColor: "#0d6efd",
                color: "#fff",
                padding: "6px 10px",
                border: "1px solid #084298",
                borderRadius: "4px",
                width: "100%",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                Total Trips: ({payoutSummary.totalTrips || 0})
              </div>{" "}
            </div>
          ),
          selector: (row) => row.trips,
          center: true,
        },

        {
          name: (
            <b>Fixed Trips ({response.data.payoutSummary?.fixedTrips || 0})</b>
          ),
          selector: (row) => row.fixedTrips || "-",
          center: true,
        },
        {
          // name: <b>Total PP Trips</b>,
          name: <b>PP Trips ({response.data.payoutSummary?.ppTrips || 0})</b>,
          selector: (row) => row.ppTrips || "-",
          center: true,
        },
        {
          name: (
            <b>
              Fixed Payout (
              {Number(
                response.data.payoutSummary?.totalFixedPayout || 0,
              ).toLocaleString()}
              )
            </b>
          ),
          selector: (row) =>
            row.fixedPayout ? Number(row.fixedPayout).toFixed(2) : "-",
          center: true,
        },
        {
          name: (
            <b>
              PP Payout (
              {Number(
                response.data.payoutSummary?.totalPPPayout || 0,
              ).toLocaleString()}
              )
            </b>
          ),
          selector: (row) =>
            row.ppPayout ? Number(row.ppPayout).toFixed(2) : "-",
          center: true,
        },
      ];

      /* 🔹 DYNAMIC DATE COLUMNS */
      const dynamicColumns = [];

      sortedDates.forEach((date, index) => {
        const headerBg = index % 2 === 0 ? "#eef4ff" : "#f9fafb";

        const headerStyle = {
          backgroundColor: headerBg,
          padding: "6px 4px",
          borderRadius: "4px",
          textAlign: "center",
        };
        const totalsForDate = apiDateTotals.find((d) => d.date === date);

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px" }}>
                Fixed Trips: {totalsForDate?.fixedTrips || 0}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) => Number(row.dateSummary?.[date]?.fixedTrips || 0),
        });

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px" }}>
                PP Trips: {totalsForDate?.ppTrips || 0}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) => Number(row.dateSummary?.[date]?.ppTrips || 0),
        });
        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px" }}>
                Fixed Payout: ₹
                {Number(totalsForDate?.fixed || 0).toLocaleString()}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) =>
            Number(row.dateSummary?.[date]?.fixedPayout || 0).toFixed(2),
        });

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px" }}>
                PP Payout: ₹{Number(totalsForDate?.pp || 0).toLocaleString()}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) =>
            Number(row.dateSummary?.[date]?.ppPayout || 0).toFixed(2),
        });

        // dynamicColumns.push({
        //   name: (
        //     <div
        //       style={{
        //         ...headerStyle,
        //         backgroundColor: "#fff3cd",
        //         border: "2px solid #ffc107",
        //         borderRadius: "6px",
        //         padding: "4px 8px",
        //       }}
        //     >
        //       <div
        //         style={{
        //           fontSize: "12px",
        //           fontWeight: "bold",
        //           color: "#856404",
        //         }}
        //       >
        //         Total Trips: {totalsForDate?.trips || 0}
        //       </div>
        //       <b>{moment(date).format("D MMM YYYY")}</b>
        //     </div>
        //   ),
        //   center: true,
        //   minWidth: "120px",
        //   selector: (row) => Number(row.dateSummary?.[date]?.trips || 0),
        // });

        dynamicColumns.push({
          name: (
            <div
              style={{
                ...headerStyle,
                backgroundColor: "#fff3cd",
                border: "2px solid #ffc107",
                borderRadius: "6px",
                padding: "6px 10px",
                minWidth: "145px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "#856404",
                  lineHeight: "18px",
                  whiteSpace: "nowrap",
                }}
              >
                Total Trips: {totalsForDate?.trips || 0}
              </div>

              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "#856404",
                  lineHeight: "18px",
                  whiteSpace: "nowrap",
                }}
              >
                CrossUtilized:
                {totalsForDate?.utilizedVehicleCount || 0}
              </div>

              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "#856404",
                  lineHeight: "18px",
                  whiteSpace: "nowrap",
                }}
              >
                Vehicles: {totalsForDate?.vehicleCount || 0}
              </div>

              <b style={{ whiteSpace: "nowrap" }}>
                {moment(date).format("D MMM YYYY")}
              </b>
            </div>
          ),
          center: true,
          minWidth: "155px",
          selector: (row) => Number(row.dateSummary?.[date]?.trips || 0),
        });

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                Clients
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),

          cell: (row) => {
            const dateData = row.dateSummary?.[date] || {};

            const clients = Array.isArray(dateData.clients)
              ? dateData.clients
              : [];

            const clientDetails = clients
              .map(
                (client) =>
                  `${client.clientName || "Unknown Client"} (${client.trips || 0} trips)`,
              )
              .join("\n");

            return (
              <div
                title={clientDetails || "No client details available"}
                style={{
                  width: "100%",
                  maxWidth: "170px",
                  fontSize: "10px",
                  lineHeight: "14px",
                  textAlign: "left",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflowWrap: "anywhere",
                  maxHeight: "28px",
                }}
              >
                {clients.length > 0
                  ? clients.map((client, index) => (
                      <div key={client.clientId || index}>
                        {client.clientName || "Unknown Client"} (
                        {client.trips || 0} trips)
                      </div>
                    ))
                  : "-"}
              </div>
            );
          },

          center: true,
          width: "190px",
          minWidth: "190px",
          maxWidth: "190px",
        });

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                Total: ₹{Number(totalsForDate?.total || 0).toLocaleString()}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) =>
            Number(row.dateSummary?.[date]?.payout || 0).toFixed(2),
        });
      });

      setColumns([...staticColumns, ...dynamicColumns]);
    } catch (error) {
      console.error("Vehicle Revenue Error:", error);
    }
  };

  /* ✅ INITIAL LOAD */
  useEffect(() => {
    fetchData(1);
  }, []);

  const sendVehicleRevenueMail = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("Authentication token not found.");
        return;
      }

      setSendingMail(true);

      const currentMonth = moment().month() + 1;
      const currentYear = moment().year();

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/orders/vehicle-payout/summary`,
        {
          params: {
            page: 1,
            limit: 100000,
            month: currentMonth,
            year: currentYear,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.data?.success) {
        alert("Unable to generate Vehicle Revenue summary.");
        return;
      }

      const summary = response.data.payoutSummary || {};

      const mailResponse = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/orders/vehicle-payout/send-summary-mail`,
        {
          month: selectedMonth,
          year: selectedYear,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (mailResponse.data?.success) {
        setShowMailSuccess(true);
      } else {
        alert("Failed to send Vehicle Revenue summary.");
      }
    } catch (error) {
      console.error("Send Vehicle Revenue mail error:", error);

      alert(
        error.response?.data?.message ||
          "Failed to send Vehicle Revenue summary.",
      );
    } finally {
      setSendingMail(false);
    }
  };

  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("No token found");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/vehicle-types`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data?.vehicleType) {
          setVehicleTypes(response.data.vehicleType);
        } else {
          setVehicleTypes([]);
        }
      } catch (error) {
        console.error("Error fetching vehicle types:", error);
        setVehicleTypes([]);
      }
    };

    fetchVehicleTypes();
  }, []);

  useEffect(() => {
    const fetchLeasingPartners = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("No token found");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v3/leases`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.status === 200) {
          const partnerMap = new Map();

          (response.data.leases || []).forEach((lease) => {
            if (
              lease.leasing_partner &&
              !partnerMap.has(lease.leasing_partner)
            ) {
              partnerMap.set(lease.leasing_partner, {
                value: lease.leasing_partner,
                label: lease.leasing_partner,
              });
            }
          });

          setLeasingPartnerOptions([...partnerMap.values()]);
        }
      } catch (error) {
        console.error("Error fetching leasing partners:", error);
        setLeasingPartnerOptions([]);
      }
    };

    fetchLeasingPartners();
  }, []);

  const exportToExcel = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("Authentication token not found.");
        return;
      }

      setLoadingExport(true);

      const {
        month,
        year,
        search,
        fromDate,
        toDate,
        companies = [],
        feetValues = [],
        statuses = [],
        leasingPartners = [],
      } = appliedFilters;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/orders/vehicle-payout/summary`,
        {
          params: {
            page: 1,
            limit: 100000,

            search: search || "",

            ...(companies.length > 0 && {
              company: companies.join(","),
            }),

            ...(feetValues.length > 0 && {
              feet: feetValues.join(","),
            }),
            ...(leasingPartners.length > 0 && {
              leasingPartner: leasingPartners.join(","),
            }),

            ...(statuses.length > 0 && {
              status: statuses.join(","),
            }),

            // If From Date + To Date are selected
            ...(fromDate && toDate
              ? {
                  fromDate,
                  toDate,
                }
              : {
                  // Otherwise use selected month/year
                  month: month || selectedMonth,
                  year: year || selectedYear,
                }),
          },

          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.data?.success) {
        alert("Unable to export Vehicle Revenue data.");
        return;
      }

      const payoutSummary = response.data.payoutSummary || {};
      const vehicleSummary = payoutSummary.vehicleSummary || [];

      if (!vehicleSummary.length) {
        alert("No data found for the selected filters.");
        return;
      }

      // Collect all dates returned by API
      const dateSet = new Set();

      vehicleSummary.forEach((vehicle) => {
        Object.keys(vehicle.dateSummary || {}).forEach((date) => {
          dateSet.add(date);
        });
      });

      const sortedDates = Array.from(dateSet).sort(
        (a, b) => new Date(a) - new Date(b),
      );

      // Convert API response into Excel rows
      const excelData = vehicleSummary.map((vehicle, index) => {
        const row = {
          "S.No": index + 1,
          "Vehicle Number": vehicle.numberplate || "-",
          "Leasing Partner": vehicle.leasingPartner || "-",
          Company: vehicle.company || "-",
          Status: vehicle.status || "-",
          "Vehicle Feet": vehicle.feet || "-",

          "Lease Amount": Number(vehicle.leaseAmount || 0),
          "Average Lease Amount": Number(vehicle.averageLeaseAmount || 0),

          "Units Consumed": Number(vehicle.unitsConsumed || 0),
          "Charging Sessions": Number(vehicle.chargingSessions || 0),
          "Charging Amount": Number(vehicle.chargingAmount || 0),

          "Average Payout": Number(vehicle.averagePayout || 0),

          "Total Trips": Number(vehicle.trips || 0),
          "Total Payout": Number(vehicle.payout || 0),

          "Fixed Trips": Number(vehicle.fixedTrips || 0),
          "PP Trips": Number(vehicle.ppTrips || 0),

          "Fixed Payout": Number(vehicle.fixedPayout || 0),
          "PP Payout": Number(vehicle.ppPayout || 0),
        };

        // Add date-wise columns
        sortedDates.forEach((date) => {
          const summary = vehicle.dateSummary?.[date] || {};
          const formattedDate = moment(date).format("DD-MM-YYYY");
          const clients = Array.isArray(summary.clients) ? summary.clients : [];
          row[`${formattedDate} - Fixed Trips`] = Number(
            summary.fixedTrips || 0,
          );

          row[`${formattedDate} - PP Trips`] = Number(summary.ppTrips || 0);

          row[`${formattedDate} - Fixed Payout`] = Number(
            summary.fixedPayout || 0,
          );

          row[`${formattedDate} - PP Payout`] = Number(summary.ppPayout || 0);

          row[`${formattedDate} - Total Trips`] = Number(summary.trips || 0);

          row[`${formattedDate} - Total Payout`] = Number(summary.payout || 0);

          row[`${formattedDate} - Unique Clients`] =
            summary.clientCount ?? clients.length;

          row[`${formattedDate} - Client Details`] = clients
            .map((client) => `${client.clientName} (${client.trips} trips)`)
            .join(", ");
        });

        return row;
      });

      // Create Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicle Revenue");

      // Download
      XLSX.writeFile(
        workbook,
        `vehicle_revenue_filtered_${moment().format("DD-MM-YYYY_HH-mm")}.xlsx`,
      );
    } catch (error) {
      console.error("Error exporting Vehicle Revenue:", error);

      alert(
        error.response?.data?.message || "Failed to export Vehicle Revenue.",
      );
    } finally {
      setLoadingExport(false);
    }
  };

  const companyOptions = [
    ...new Set(
      vehicleTypes.map((item) => item.company?.trim()).filter(Boolean),
    ),
  ].sort();

  const feetOptions = [
    ...new Set(
      vehicleTypes
        .map((item) => Number(item.feet))
        .filter((item) => !Number.isNaN(item)),
    ),
  ].sort((a, b) => a - b);

  return (
    <>
      <Layouts />

      <div className="container-fluid">
        <div className="row my-3">
          <div className="col-12">
            <div className="card" style={{ borderTop: "2px solid #4723d9" }}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">{title}</h4>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary btn-sm text-white"
                      onClick={sendVehicleRevenueMail}
                      disabled={sendingMail}
                    >
                      {sendingMail ? "Sending..." : "✉ Send Mail"}
                    </button>
                    <button
                      className="btn btn-success btn-sm text-white"
                      onClick={exportToExcel}
                      disabled={loadingExport}
                    >
                      {loadingExport ? "Exporting..." : "Export to Excel"}
                    </button>
                  </div>
                </div>
              </div>
              {showMailSuccess && (
                <div
                  className="modal fade show"
                  style={{
                    display: "block",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                  }}
                  tabIndex="-1"
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow">
                      <div className="modal-body text-center p-4">
                        {/* Success Icon */}
                        <div
                          className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            backgroundColor: "#e8f5e9",
                            color: "#2e7d32",
                            fontSize: "30px",
                          }}
                        >
                          ✓
                        </div>

                        <h5 className="mb-2">Email Sent Successfully</h5>

                        <p className="text-muted mb-4">
                          Vehicle Revenue summary has been sent successfully.
                        </p>

                        <button
                          type="button"
                          className="btn btn-primary px-4"
                          onClick={() => setShowMailSuccess(false)}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="card-body">
                <DataTable_Component
                  columns={columns}
                  fetchData={fetchData}
                  onFiltersApplied={setAppliedFilters}
                  totalPayout={totalPayout}
                  averagePayout={averagePayout}
                  totalTrips={totalTrips}
                  totalFixedTrips={totalFixedTrips}
                  totalPpTrips={totalPpTrips}
                  companyOptions={companyOptions}
                  leasingPartnerOptions={leasingPartnerOptions}
                  feetOptions={feetOptions}
                  totalUnitsConsumed={totalUnitsConsumed}
                  averageUnitsConsumed={averageUnitsConsumed}
                  totalChargingSessions={totalChargingSessions}
                  totalChargingAmount={totalChargingAmount}
                  averageChargingAmount={averageChargingAmount}
                  totalLeaseAmount={totalLeaseAmount}
                  averageLeaseAmount={averageLeaseAmount}
                  paginationData={{
                    data: apiData,
                    page: pagination.page,
                    limit: pagination.limit,
                    totalClients: pagination.totalVehicles, // 🔹 matches component API
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
